import { AssetStatus } from "@hydrologiq/gql-experience/graphql"
import { KG_PER_NL } from "./hydrogenUnits"

/** One electrolyser within a stack. */
export interface ElectrolyserStackUnit {
  id: string
  name: string
  status: AssetStatus | null
  /** Own fill rate in NL/h, or null when the unit reports no flow attribute. */
  flowRate: number | null
}

/** A parent electrolyser and the units that make up its stack. */
export interface ElectrolyserStack {
  parentAssetId: string
  parentAssetName: string
  units: ElectrolyserStackUnit[]
}

/**
 * The units whose statuses decide the stack's status: the children when there are any, otherwise
 * the parent on its own.
 *
 * The parent of a real stack is a grouping asset created to hold the children (see prod Burnaston's
 * "Burnaston Electrolyser Stack"), and a freshly created asset defaults to `AVAILABLE` with nothing
 * to ever update it. Letting that count as a unit is actively harmful: with 5 of 10 children in
 * error and 5 in standby, the parent's stray `AVAILABLE` broke the standby unanimity and the whole
 * stack reported UNKNOWN — the one answer that tells an operator nothing.
 *
 * The parent stays in `units` regardless, because it owns the group's summed flow and belongs in the
 * popover listing. It just doesn't get a vote.
 */
export function statusUnits(stack: ElectrolyserStack): ElectrolyserStackUnit[] {
  const children = stack.units.filter((unit) => unit.id !== stack.parentAssetId)
  return children.length > 0 ? children : stack.units
}

/**
 * Roll a stack's units up to one status.
 *
 * Product rules, in this exact order — the governing idea is that the stack is still "ok" while
 * half the units are ok, so `error` needs a strict majority to win:
 *
 *   more than half in error  → error
 *   all in startup           → startup
 *   all in standby           → standby
 *   all in testing           → testing
 *   at least one active      → active
 *   otherwise                → unknown
 *
 * Once error has failed the majority test, the remaining branches are evaluated over the NON-ERROR
 * units: a minority fault shouldn't stop the stack reporting what its working units are doing, so
 * [error, standby] is standby rather than falling through to unknown. A majority of errors still
 * wins outright — [error, error, standby] is error.
 *
 * TODO: core change OUTSTANDING. 
 * Child assets reach the frontend without a status, 
 * so in practice only the parent's status feeds these rules until that change is in place.
 *
 * NOTE: the rules name `active` only, so a unit sitting in `FILLING` does not satisfy the
 * at-least-one-active branch and a stack of purely filling units resolves to `unknown`. That looks
 * unintended — filling is a running state, and the per-unit card already treats Filling and Active
 * alike — but it is implemented exactly as specified rather than quietly widened. Add
 * `AssetStatus.Filling` to `RUNNING` below if it should count.
 */
const RUNNING: AssetStatus[] = [AssetStatus.Active]

export function deriveStackStatus(units: Pick<ElectrolyserStackUnit, "status">[]): AssetStatus {
  // Units of unknown status take no part in the roll-up — they neither dilute an error majority nor
  // break unanimity. This matters because the system view returns child assets with only id, name
  // and class: a stack of one known-Standby parent plus three status-less children must still read
  // Standby, and a known-Error parent must still read Error rather than being outvoted by silence.
  const known = units
    .map((u) => u.status)
    .filter((s): s is AssetStatus => s != null)

  // "all in X" is vacuously true for an empty set, which would report a confident status for nothing
  // at all — so a stack with no known status is unknown before any rule is applied.
  if (known.length === 0) return AssetStatus.Unknown

  const errors = known.filter((x) => x === AssetStatus.Error).length
  if (errors > known.length / 2) return AssetStatus.Error

  // Errors are a minority from here on, so they no longer speak for the stack. Judging the rest on
  // their own is what lets [error, standby] report standby. This can't be empty: if every known unit
  // were in error, the majority test above would already have returned.
  const working = known.filter((x) => x !== AssetStatus.Error)
  const all = (s: AssetStatus) => working.every((x) => x === s)

  if (all(AssetStatus.Startup)) return AssetStatus.Startup
  if (all(AssetStatus.Standby)) return AssetStatus.Standby
  if (all(AssetStatus.Testing)) return AssetStatus.Testing
  if (working.some((x) => RUNNING.includes(x))) return AssetStatus.Active
  return AssetStatus.Unknown
}

/**
 * Total fill rate across the stack in NL/h. Children fill too, so this is the sum of every unit
 * that reports a flow, and always a number — the card shows the line unconditionally, reading 0
 * when nothing is flowing or nothing reports.
 */
export function stackFlowRate(units: Pick<ElectrolyserStackUnit, "flowRate">[]): number {
  return units.reduce((total, u) => {
    const rate = u.flowRate
    return total + (rate != null && Number.isFinite(rate) ? rate : 0)
  }, 0)
}

/**
 * Split a flow into a headline reading and a supporting one, e.g. `1250 NL/h` / `0.11 kg/h`.
 *
 * Both units are shown rather than alternating on a timer: the two readings for the same flow are
 * wildly different in magnitude (1000 NL/h is only ~0.09 kg/h), so a figure that swapped units
 * under a fixed label would invite someone glancing at it to read the wrong number.
 *
 * NL/h leads because kg/h is the weaker of the two at these rates — it sits near zero and needs
 * decimals to say anything, which makes for a poor headline figure.
 */
export function flowRateParts(nlPerHour: number): { primary: string; secondary: string } {
  const kgPerHour = nlPerHour * KG_PER_NL
  return {
    primary: `${Math.round(nlPerHour)} NL/h`,
    secondary: `${kgPerHour.toFixed(2)} kg/h`,
  }
}
